// @flow
import React, { Component, type Node } from 'react';
import styled from 'styled-components';

import { mergeClassNames } from 'utils/functions';

type TableProps = {
    /** will be appended to the classes
     * use bs' 'table-striped', 'list-items-table', 'table-sm', etc
     * */
    className: string,
    /** array of column headers (ex: 'First', 'Last', 'Phone', ...) */
    headers: Array<Node>,
    /** array of row arrays */
    rows: Array<Array<Node>>,
    /** view headings inline? if true, column headings are not displayed. The state will also set inlineHeadings for mobile display */
    inlineHeadings: boolean,
    /** optional light or dark background for thead */
    headerFlavor: 'light' | 'dark' | '',
    /** optional callback for knowing when table has gone mobile because it does not depend on media queries */
    inlineHeadingsCallback: boolean => void,
};

type TableState = {
    className: string,
    headerState: {
        inlineHeadings: boolean,
    },
    name: string,
};

const desktopState = {
    className: '',
    headerState: {
        inlineHeadings: false,
    },
    name: 'desktop',
};

const mobileState = {
    className: 'table-striped table-sm cell-d-flex',
    headerState: {
        inlineHeadings: true,
    },
    name: 'mobile',
};

const StyledWrapper = styled.div`
    width: 100%;
    position: relative;
    margin-bottom: 1rem;

    table,
    .table {
        margin-bottom: 0;
        min-width: 100%;
        width: inherit;
    }
    tbody {
        height: 100%;
        overflow-y: auto;
    }
    .table.thead-light .thead-dark th {
        color: inherit;
        background-color: inherit;
        border: none;
    }
    .cell-d-flex {
        td {
            display: flex;
            .text-right {
                display: inline;
                float: inherit;
            }
        }
    }
    td,
    tr {
        transition: all 0.34s;
    }
`;

export class Table extends Component<TableProps, TableState> {
    static defaultProps = {
        inlineHeadings: false,
        className: 'table-hover',
        rows: [],
        headers: [],
        headerFlavor: 'dark',
        inlineHeadingsCallback: () => {},
    };

    constructor(props: TableProps) {
        super(props);
        this.state = desktopState;
        this.wrapperRef = React.createRef();
        this.tableRef = React.createRef();
        this.updateLayout = this.updateLayout.bind(this);
        this.updateLayoutOnTimeout = this.updateLayoutOnTimeout.bind(this);
        this.setMobileStateIfNeeded = this.setMobileStateIfNeeded.bind(this);
        this.setDesktopStateIfMobile = this.setDesktopStateIfMobile.bind(this);
        this.isTableTooWide = this.isTableTooWide.bind(this);
        this.setMobileState = this.setMobileState.bind(this);
        this.setDesktopState = this.setDesktopState.bind(this);
    }

    setMobileState() {
        this.setState({ ...this.state, ...mobileState });
    }

    setDesktopState() {
        this.setState(
            { ...this.state, ...desktopState },
            this.setMobileStateIfNeeded()
        );
    }

    isTableTooWide() {
        const wrapper = this.wrapperRef.current;
        const table = this.tableRef.current;
        return table.offsetWidth > wrapper.offsetWidth;
    }

    setMobileStateIfNeeded() {
        if (this.isTableTooWide()) {
            this.setMobileState();
            this.props.inlineHeadingsCallback(true);
        }
    }

    setDesktopStateIfMobile() {
        if (this.state.name === 'mobile') {
            this.setDesktopState();
            this.props.inlineHeadingsCallback(false);
        }
    }

    updateLayout() {
        this.setDesktopStateIfMobile();
        this.setMobileStateIfNeeded();
    }

    updateLayoutOnTimeout() {
        setTimeout(this.updateLayout(), 340);
    }

    componentDidMount() {
        window.addEventListener('resize', this.updateLayoutOnTimeout);
        this.updateLayout();
    }

    componentDidUpdate(prevProps: TableProps, prevState: TableState) {
        const updated = !prevProps.rows.length && this.props.rows.length;
        if (updated) {
            this.updateLayout();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateLayoutOnTimeout);
    }

    render() {
        const { headerFlavor, headers, rows, className, inlineHeadings, emptyText } = this.props;
        const { headerState } = this.state;
        let mergedClasses = mergeClassNames('table', this.state.className, className);
        const displayColumnHeadings = inlineHeadings
            ? false
            : !headerState.inlineHeadings;
        return (
            <StyledWrapper ref={this.wrapperRef}>
                <table className={mergedClasses} ref={this.tableRef}>
                    {rows.length ? (
                        <>
                            {displayColumnHeadings && (
                                <TableHeader headers={headers} flavor={headerFlavor} />
                            )}
                            <tbody>
                                {rows.map((row, index) =>
                                    (headerState.inlineHeadings || inlineHeadings ? (
                                        <InlineHeadingsTableRow
                                            key={index}
                                            row={row}
                                            headers={headers}
                                        />
                                    ) : (
                                        <TableRow key={index} row={row} />
                                    ))
                                )}
                            </tbody>
                        </>
                    ) : (
                        <EmptyTableHeader text={emptyText || "No results"}/>
                    )}
                </table>
            </StyledWrapper>
        );
    }
}

export const EmptyTableHeader = ({ text }) => (
    <thead className='thead-light'>
        <tr>
            <th>{text}</th>
        </tr>
    </thead>
);

type TableHeaderProps = {
    headers: Array<Node>,
    flavor: string,
};

export class TableHeader extends Component<TableHeaderProps> {
    static defaultProps = {
        flavor: 'dark',
    };

    render() {
        const { flavor, headers } = this.props;
        const className = flavor ? `thead-${flavor}` : '';

        return (
            <thead className={className}>
                <tr>
                    {headers.map((name, index) => (
                        <th key={index}>{name}</th>
                    ))}
                </tr>
            </thead>
        );
    }
}

type TableRowProps = {
    row: Array<Node>,
};

export class TableRow extends Component<TableRowProps> {
    render() {
        return (
            <tr>
                {this.props.row.map((name, index) => (
                    <td key={index}>{name}</td>
                ))}
            </tr>
        );
    }
}

type InlineHeadingsProps = {
    headers: Array<Node>,
    row: Array<Node>,
};

export const InlineHeadingsTableRow = ({
    headers = [],
    row = [],
}: InlineHeadingsProps) => (
    <TableRow
        row={row.map((cell, i) => (
            <span key={i} className='d-flex flex-fill'>
                {headers[i] && (
                    <span className='flex-fill'>
                        <strong>{headers[i]}:</strong>
                    </span>
                )}
                <span className='w-100'>{cell}</span>
            </span>
        ))}
    />
);

export default Table;
